import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlusIcon, XMarkIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { suggestionService } from '../services/suggestionService';
import { Suggestion } from '../types/firestore';
import { isApprovedServiceAdmin } from '../utils/accessControl';
import { Alert, Badge, Button, Card, Modal, Spinner } from '../components/common/UIComponents';
import SuggestionForm from '../components/Suggestions/SuggestionForm';
import {
  SuggestionCard,
  SuggestionDetailPanel,
} from '../components/Suggestions/SuggestionWorkspace';
import {
  matchesSuggestionBucket,
  type SuggestionBucket,
  SUGGESTION_BUCKETS,
} from '../utils/suggestionBuckets';
import {
  getSuggestionDefaultView,
  normalizeSuggestionStatus,
  type SuggestionViewMode,
} from '../utils/suggestionWorkflow';
import {
  getSuggestionHostSelectionKey,
  getSuggestionIdentity,
  isSuggestionInVisibleSet,
  isQuerySelectionPendingOutsideVisible,
  resolveSuggestionHostSelection,
  type SuggestionHostSelectionState,
} from '../utils/suggestionHostSelection';
import { getSuggestionActionErrorMessage } from '../utils/suggestionActionErrors';
import { sortSuggestionsForHost } from '../utils/suggestionOrdering';
import { notificationService } from '../services/notificationService';

type SuggestionParticipation = Awaited<
  ReturnType<typeof suggestionService.getSuggestionParticipation>
>;

const EMPTY_VIEW: SuggestionViewMode = 'original';
const PREFERRED_DETAIL_VIEW: SuggestionViewMode = 'technical';
const MOBILE_BUCKET_KEYS: SuggestionBucket[] = ['all', 'voting', 'history'];
const INLINE_DETAIL_MEDIA_QUERY = '(max-width: 1279px)';

const SuggestionsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, claims } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<SuggestionBucket>('all');
  const [selection, setSelection] = useState<SuggestionHostSelectionState>({
    suggestionId: '',
    view: EMPTY_VIEW,
  });
  const [selectedParticipation, setSelectedParticipation] = useState<SuggestionParticipation>({
    myVote: null,
    eligibleToVote: false,
    voteUpdatedAt: null,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const createModalTitleId = useId();
  const createTriggerRef = useRef<HTMLButtonElement | null>(null);
  const createInitialFocusRef = useRef<HTMLInputElement | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [voteLoading, setVoteLoading] = useState(false);
  const lastAppliedQuerySelectionKeyRef = useRef('');
  const handledNotificationReadbacksRef = useRef<Set<string>>(new Set());
  const [isInlineDetailViewport, setIsInlineDetailViewport] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(INLINE_DETAIL_MEDIA_QUERY).matches;
  });

  const canCreateSuggestion = isApprovedServiceAdmin(userProfile, claims);
  const isAdminAccount = Boolean((claims as any)?.isAdmin || userProfile?.role === 'admin');

  const queryState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      suggestionId: params.get('suggestionId')?.trim() || '',
      suggestionView:
        params.get('suggestionView')?.trim() === 'technical' ? 'technical' : 'original',
    } as const;
  }, [location.search]);

  const visibleSuggestions = useMemo(
    () => suggestions.filter((item) => matchesSuggestionBucket(item, filter)),
    [filter, suggestions]
  );
  const autoOpenSuggestionId = useMemo(() => {
    if (!isInlineDetailViewport) {
      return '';
    }

    const highestPriorityVotingSuggestion = visibleSuggestions.find(
      (item) => normalizeSuggestionStatus(item.status) === 'voting_open'
    );

    return highestPriorityVotingSuggestion
      ? getSuggestionIdentity(highestPriorityVotingSuggestion)
      : '';
  }, [isInlineDetailViewport, visibleSuggestions]);
  const pendingQuerySelectionOutsideVisible = useMemo(() => {
    return isQuerySelectionPendingOutsideVisible({
      suggestions,
      visibleSuggestions,
      querySelection: {
        suggestionId: queryState.suggestionId,
        view: queryState.suggestionView,
      },
      lastAppliedQuerySelectionKey: lastAppliedQuerySelectionKeyRef.current,
      loading,
    });
  }, [
    loading,
    queryState.suggestionId,
    queryState.suggestionView,
    suggestions,
    visibleSuggestions,
  ]);

  const selectedVisibleSuggestion = useMemo(
    () =>
      visibleSuggestions.find((item) => getSuggestionIdentity(item) === selection.suggestionId) ||
      null,
    [selection.suggestionId, visibleSuggestions]
  );

  const highlightedSuggestionMissing =
    Boolean(queryState.suggestionId) &&
    !loading &&
    !suggestions.some((item) => getSuggestionIdentity(item) === queryState.suggestionId);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const feed = await suggestionService.getSuggestionsFeed({
        currentUserId: currentUser?.uid || null,
        includeMyVotes: true,
        includeArchived: true,
      });
      setSuggestions(sortSuggestionsForHost(feed));
    } catch (feedError) {
      console.error('Nie udało się pobrać sugestii:', feedError);
      setError('Nie udało się załadować listy sugestii.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    void loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(INLINE_DETAIL_MEDIA_QUERY);
    const handleChange = () => setIsInlineDetailViewport(mediaQuery.matches);
    handleChange();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (pendingQuerySelectionOutsideVisible) {
      setFilter('all');
      return;
    }

    const querySelectionKey = getSuggestionHostSelectionKey({
      suggestionId: queryState.suggestionId,
      view: queryState.suggestionView,
    });
    const allowQuerySelection =
      Boolean(queryState.suggestionId) &&
      querySelectionKey !== lastAppliedQuerySelectionKeyRef.current &&
      isSuggestionInVisibleSet(visibleSuggestions, queryState.suggestionId);

    setSelection((currentSelection) => {
      const nextSelection = resolveSuggestionHostSelection({
        visibleSuggestions,
        currentSelection,
        querySelection: {
          suggestionId: queryState.suggestionId,
          view: queryState.suggestionView,
        },
        defaultView: PREFERRED_DETAIL_VIEW,
        allowQuerySelection,
        autoSelectSuggestionId: autoOpenSuggestionId,
        fallbackToFirstVisible: !isInlineDetailViewport,
      });

      if (allowQuerySelection && nextSelection.suggestionId === queryState.suggestionId) {
        lastAppliedQuerySelectionKeyRef.current = querySelectionKey;
      }

      if (
        nextSelection.suggestionId === currentSelection.suggestionId &&
        nextSelection.view === currentSelection.view
      ) {
        return currentSelection;
      }

      return nextSelection;
    });
  }, [
    pendingQuerySelectionOutsideVisible,
    queryState.suggestionId,
    queryState.suggestionView,
    autoOpenSuggestionId,
    isInlineDetailViewport,
    visibleSuggestions,
  ]);

  useEffect(() => {
    if (!selection.suggestionId || !currentUser?.uid) {
      setSelectedParticipation({
        myVote: null,
        eligibleToVote: false,
        voteUpdatedAt: null,
      });
      return;
    }

    let active = true;
    setDetailLoading(true);

    suggestionService
      .getSuggestionParticipation(selection.suggestionId, currentUser.uid)
      .then((result) => {
        if (!active) {
          return;
        }
        setSelectedParticipation(result);
      })
      .catch((participationError) => {
        console.error('Nie udało się pobrać informacji o głosie:', participationError);
        if (active) {
          setSelectedParticipation({
            myVote: null,
            eligibleToVote: false,
            voteUpdatedAt: null,
          });
        }
      })
      .finally(() => {
        if (active) {
          setDetailLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [currentUser?.uid, selection.suggestionId]);

  useEffect(() => {
    if ((!selection.suggestionId && loading) || pendingQuerySelectionOutsideVisible) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (!selection.suggestionId) {
      if (!params.has('suggestionId') && !params.has('suggestionView')) {
        return;
      }

      params.delete('suggestionId');
      params.delete('suggestionView');
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
      return;
    }

    if (
      params.get('suggestionId') === selection.suggestionId &&
      params.get('suggestionView') === selection.view
    ) {
      return;
    }

    params.set('suggestionId', selection.suggestionId);
    params.set('suggestionView', selection.view);
    lastAppliedQuerySelectionKeyRef.current = getSuggestionHostSelectionKey({
      suggestionId: selection.suggestionId,
      view: selection.view,
    });
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [
    loading,
    location.pathname,
    location.search,
    navigate,
    selection.suggestionId,
    selection.view,
    pendingQuerySelectionOutsideVisible,
  ]);

  useEffect(() => {
    const notificationId = new URLSearchParams(location.search).get('notificationId')?.trim() || '';
    if (
      !currentUser?.uid ||
      loading ||
      Boolean(error) ||
      pendingQuerySelectionOutsideVisible ||
      !notificationId ||
      !queryState.suggestionId ||
      selection.suggestionId !== queryState.suggestionId ||
      selection.view !== queryState.suggestionView ||
      !selectedVisibleSuggestion
    ) {
      return;
    }

    const readbackKey = `${currentUser.uid}:${notificationId}`;
    if (handledNotificationReadbacksRef.current.has(readbackKey)) {
      return;
    }
    handledNotificationReadbacksRef.current.add(readbackKey);
    notificationService
      .markSuggestionNotificationAsRead({
        notificationId,
        suggestionId: queryState.suggestionId,
        suggestionView: queryState.suggestionView,
        host: 'user',
      })
      .then((read) => {
        if (!read) {
          handledNotificationReadbacksRef.current.delete(readbackKey);
        }
      })
      .catch((readbackError) => {
        handledNotificationReadbacksRef.current.delete(readbackKey);
        console.error('Nie udało się potwierdzić odczytu powiadomienia sugestii:', readbackError);
      });
  }, [
    currentUser?.uid,
    error,
    loading,
    location.search,
    pendingQuerySelectionOutsideVisible,
    queryState.suggestionId,
    queryState.suggestionView,
    selectedVisibleSuggestion,
    selection.suggestionId,
    selection.view,
  ]);

  const handleSelectSuggestion = (suggestionId: string) => {
    if (!suggestionId) {
      return;
    }
    const selectedSuggestion =
      visibleSuggestions.find((item) => getSuggestionIdentity(item) === suggestionId) || null;
    setSelection((current) =>
      current.suggestionId === suggestionId
        ? { suggestionId: '', view: EMPTY_VIEW }
        : {
            suggestionId,
            view: selectedSuggestion
              ? getSuggestionDefaultView(selectedSuggestion, PREFERRED_DETAIL_VIEW)
              : EMPTY_VIEW,
          }
    );
  };

  const handleCreateSuccess = async (suggestionId: string) => {
    setCreateOpen(false);
    setSelection({
      suggestionId,
      view: EMPTY_VIEW,
    });
    await loadSuggestions();
  };

  const handleVote = async (vote: 'yes' | 'no') => {
    if (!selection.suggestionId) {
      return;
    }

    setVoteLoading(true);
    setError(null);
    try {
      await suggestionService.castSuggestionVote({
        suggestionId: selection.suggestionId,
        vote,
      });
      await loadSuggestions();
      if (currentUser?.uid) {
        const participation = await suggestionService.getSuggestionParticipation(
          selection.suggestionId,
          currentUser.uid
        );
        setSelectedParticipation(participation);
      }
    } catch (voteError: any) {
      console.error('Nie udało się zapisać głosu:', voteError);
      setError(getSuggestionActionErrorMessage(voteError));
    } finally {
      setVoteLoading(false);
    }
  };

  const renderVotingActionArea = () => {
    if (
      !selectedVisibleSuggestion ||
      selectedVisibleSuggestion.workflowVersion !== 2 ||
      selectedVisibleSuggestion.status !== 'voting_open'
    ) {
      return null;
    }

    const currentVote = selectedParticipation?.myVote || null;
    const eligible = Boolean(selectedParticipation?.eligibleToVote);

    return (
      <div className="mt-3 space-y-3 rounded-md border border-primary-dark-300/45 bg-primary-dark-100/20 p-3">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-light-400">
            Głosowanie
          </p>
          <p className="text-sm text-primary-light-200">
            {eligible
              ? 'Możesz oddać lub zmienić swój głos.'
              : 'To głosowanie jest tylko do odczytu.'}
            {currentVote ? ` Aktualnie: ${currentVote === 'yes' ? 'TAK' : 'NIE'}.` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={currentVote === 'yes' ? 'success' : 'neutral'}
            onClick={() => void handleVote('yes')}
            disabled={voteLoading || !eligible || currentVote === 'yes'}
          >
            {currentVote === 'yes'
              ? 'Twój głos: TAK'
              : currentVote === 'no'
                ? 'Zmień na TAK'
                : 'Głosuj TAK'}
          </Button>
          <Button
            type="button"
            variant={currentVote === 'no' ? 'error' : 'neutral'}
            onClick={() => void handleVote('no')}
            disabled={voteLoading || !eligible || currentVote === 'no'}
          >
            {currentVote === 'no'
              ? 'Twój głos: NIE'
              : currentVote === 'yes'
                ? 'Zmień na NIE'
                : 'Głosuj NIE'}
          </Button>
        </div>
        {!eligible && (
          <p className="text-xs leading-5 text-primary-light-400">
            Głosowanie jest zablokowane, jeśli nie należysz do snapshotu elektoratu lub Twój status
            nie jest aktywny.
          </p>
        )}
      </div>
    );
  };

  const filteredCount = visibleSuggestions.length;
  const groupCounts = useMemo(
    () =>
      SUGGESTION_BUCKETS.reduce<Record<SuggestionBucket, number>>(
        (acc, bucket) => {
          acc[bucket.key] =
            bucket.key === 'all'
              ? suggestions.length
              : suggestions.filter((item) => matchesSuggestionBucket(item, bucket.key)).length;
          return acc;
        },
        { all: 0, pre_vote: 0, voting: 0, post_vote: 0, implementation: 0, history: 0 }
      ),
    [suggestions]
  );

  const mobileBuckets = SUGGESTION_BUCKETS.filter(
    (bucket) =>
      MOBILE_BUCKET_KEYS.includes(bucket.key) &&
      (bucket.key === 'all' || groupCounts[bucket.key] > 0)
  );
  const renderFilters = (buckets: typeof SUGGESTION_BUCKETS, className: string) => (
    <div className={className}>
      {buckets.map((bucket) => (
        <Button
          key={bucket.key}
          type="button"
          variant={filter === bucket.key ? 'primary' : 'neutral'}
          size="sm"
          onClick={() => setFilter(bucket.key)}
        >
          {bucket.label}
          <span className="ml-2 rounded-full bg-black/15 px-2 py-0.5 text-[11px]">
            {groupCounts[bucket.key]}
          </span>
        </Button>
      ))}
    </div>
  );

  return (
    <div className="rs-page-gutter py-3 md:py-6">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-3.5 sm:gap-5">
        <header className="rs-brand-keyline relative overflow-hidden rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-3 pl-4 pr-12 sm:p-5 sm:pl-5">
          <button
            type="button"
            onClick={() => navigate('/app')}
            className="absolute right-3 top-3 z-10 rounded-full border border-primary-dark-300 bg-primary-dark-100/90 p-2 text-primary-light-300 transition hover:text-primary-light-100"
            aria-label="Zamknij sugestie"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>

          <div className="relative flex flex-col gap-3 sm:gap-4">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ratuj-300/12 text-ratuj-300 sm:h-10 sm:w-10">
                  <LightBulbIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-primary-light-400 sm:text-xs sm:tracking-[0.22em]">
                    Sugestie
                  </p>
                  <h1 className="mt-0.5 max-w-2xl text-lg font-semibold leading-tight text-primary-light-100 sm:mt-1 sm:text-2xl">
                    Wspólny workflow decyzji produktowych
                  </h1>
                </div>
              </div>
              <p className="max-w-2xl text-sm leading-5 text-primary-light-300 sm:leading-6">
                Zgłaszanie, opracowanie techniczne i głosowanie serwisów w jednym miejscu.
              </p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge variant="neutral" size="sm" className="shrink-0 whitespace-nowrap">
                  {filteredCount} widocznych
                </Badge>
                {canCreateSuggestion ? (
                  <Button
                    ref={createTriggerRef}
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => setCreateOpen(true)}
                    className="shrink-0 whitespace-nowrap"
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    Dodaj sugestię
                  </Button>
                ) : !isAdminAccount ? (
                  <Badge variant="info" size="sm" className="shrink-0 whitespace-nowrap">
                    Tryb tylko do odczytu
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        {!canCreateSuggestion && !isAdminAccount && (
          <Alert variant="info">
            To konto ma dostęp tylko do odczytu. Tworzenie sugestii v2 i głosowanie są dostępne
            wyłącznie dla aktywnego serviceAdmina.
          </Alert>
        )}

        {highlightedSuggestionMissing && (
          <Alert variant="warning">
            Sugestia z linku nie jest już dostępna. Pokazujemy aktualną listę.
          </Alert>
        )}

        {renderFilters(
          mobileBuckets,
          'flex gap-2 overflow-x-auto rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-1.5 sm:hidden'
        )}
        {renderFilters(
          SUGGESTION_BUCKETS,
          'hidden flex-wrap gap-2 rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-1.5 sm:flex'
        )}

        {loading && (
          <div className="flex items-center justify-center rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-6">
            <Spinner size="lg" />
            <p className="ml-3 text-primary-light-300">Ładowanie sugestii...</p>
          </div>
        )}

        {error && <Alert variant="error">{error}</Alert>}

        {!loading && !error && suggestions.length === 0 && (
          <Card className="p-6">
            <p className="text-sm text-primary-light-300">
              Nie ma jeszcze żadnych sugestii. Pierwsza zgłoszona sugestia pojawi się tutaj po
              utworzeniu.
            </p>
          </Card>
        )}

        {!loading && !error && suggestions.length > 0 && (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)]">
            <section className="space-y-3">
              <div className="space-y-3">
                {visibleSuggestions.length === 0 ? (
                  <Card className="p-4">
                    <p className="text-sm text-primary-light-300">
                      Brak sugestii w tym filtrowaniu.
                    </p>
                  </Card>
                ) : (
                  visibleSuggestions.map((suggestion) => {
                    const suggestionId = getSuggestionIdentity(suggestion);
                    const isSelected = suggestionId === selection.suggestionId;
                    const expandedDetail = isSelected ? (
                      <div className="xl:hidden">
                        {detailLoading && (
                          <div className="mb-3 flex items-center rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-3">
                            <Spinner size="md" />
                            <p className="ml-3 text-sm text-primary-light-300">
                              Ładowanie szczegółów...
                            </p>
                          </div>
                        )}
                        <SuggestionDetailPanel
                          suggestion={selectedVisibleSuggestion}
                          selectedView={selection.view}
                          onViewChange={(view) => setSelection((current) => ({ ...current, view }))}
                          currentUserVote={
                            selectedParticipation?.myVote ||
                            selectedVisibleSuggestion?.myVote ||
                            null
                          }
                          currentUserEligible={selectedParticipation?.eligibleToVote || false}
                          showEligibilityNotice={false}
                          showSystemResolutionReasons={false}
                          actionArea={renderVotingActionArea()}
                          embedded
                        />
                      </div>
                    ) : null;

                    return (
                      <SuggestionCard
                        key={suggestionId}
                        suggestion={suggestion}
                        selected={isSelected}
                        onSelect={handleSelectSuggestion}
                        activeView={isSelected ? selection.view : undefined}
                        currentUserVote={suggestion.myVote || null}
                        currentUserEligible={
                          Boolean(selectedParticipation?.eligibleToVote) && isSelected
                        }
                        expandedContent={expandedDetail}
                        inlineExpanded={isSelected}
                      />
                    );
                  })
                )}
              </div>
            </section>

            <aside className="hidden self-start space-y-3 xl:sticky xl:top-5 xl:block">
              {detailLoading && (
                <Card className="p-4">
                  <div className="flex items-center">
                    <Spinner size="md" />
                    <p className="ml-3 text-sm text-primary-light-300">Ładowanie szczegółów...</p>
                  </div>
                </Card>
              )}

              <SuggestionDetailPanel
                suggestion={selectedVisibleSuggestion}
                selectedView={selection.view}
                onViewChange={(view) => setSelection((current) => ({ ...current, view }))}
                currentUserVote={
                  selectedParticipation?.myVote || selectedVisibleSuggestion?.myVote || null
                }
                currentUserEligible={selectedParticipation?.eligibleToVote || false}
                showEligibilityNotice={false}
                showSystemResolutionReasons={false}
                actionArea={renderVotingActionArea()}
              />
            </aside>
          </div>
        )}
      </div>

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        ariaLabelledBy={createModalTitleId}
        initialFocusRef={createInitialFocusRef}
        returnFocusRef={createTriggerRef}
        className="max-w-3xl p-0"
      >
        <div className="border-b border-primary-dark-300/70 px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-primary-light-400">
                Nowa sugestia
              </p>
              <h2 id={createModalTitleId} className="text-xl font-semibold text-primary-light-100">
                Dodaj oryginał zgłoszenia
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-full border border-primary-dark-300 bg-primary-dark-100 p-2 text-primary-light-300 transition hover:text-primary-light-100"
              aria-label="Zamknij formularz"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="px-4 py-4 sm:px-6">
          <SuggestionForm
            compact
            initialFocusRef={createInitialFocusRef}
            onCancel={() => setCreateOpen(false)}
            onSuggestionAdded={(suggestionId) => {
              void handleCreateSuccess(suggestionId);
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default SuggestionsPage;
